import { useState, useEffect } from 'react';
import { Building2, MonitorSmartphone, MapPin } from 'lucide-react';
import { api } from '../lib/api.jsx';
import { ComitClientPicker } from '../components/ComitClientPicker.jsx';
import { useComitConfig } from '../lib/comit.jsx';

const EMPTY_FORM = {
  name: '',
  contactEmail: '',
  contactPhone: '',
  address: '',
  notes: '',
  comitClientId: 0,
  comitClientName: '',
};

const inputCls =
  'w-full px-3 py-2.5 bg-sidebar border border-rule rounded-lg text-fg-2 text-[13px] outline-none box-border';
const labelCls = 'block text-[11px] text-muted-2 mb-1.5';
const btnPrimary =
  'px-5 py-2.5 rounded-lg border-none cursor-pointer font-semibold text-[13px] bg-gradient-to-br from-blue-500 to-violet-500 text-white disabled:opacity-60';
const btnSecondary =
  'px-5 py-2.5 rounded-lg cursor-pointer font-medium text-[13px] bg-transparent border border-rule text-muted hover:bg-card-hi';
const pageBtn = (active, disabled) =>
  `rounded-lg px-3 py-1 text-[11px] border transition-colors ${disabled ? 'opacity-40 cursor-default' : 'cursor-pointer'} ${
    active
      ? 'bg-blue-500/15 border-blue-500 text-blue-400 font-semibold'
      : 'bg-card border-rule-2 text-muted hover:bg-card-hi hover:text-fg-2'
  }`;

const PAGE_SIZE = 24; // multiple de 2 et 3 : la dernière rangée reste pleine en 2 et 3 colonnes

export function ClientsView({ onSelectClient, can }) {
  const comit = useComitConfig();
  const [clients, setClients] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  const load = async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    // `page` déclenche la réponse paginée { items, total, … } côté serveur.
    params.set('page', page);
    params.set('pageSize', PAGE_SIZE);
    const r = await api.get('/clients?' + params.toString());
    if (r) {
      setClients(r.items || []);
      setTotal(r.total || 0);
      setTotalPages(r.totalPages || 0);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, [search, page]);

  // Une recherche qui réduit le nombre de pages peut laisser `page` hors bornes :
  // on revient à la première page à chaque changement de recherche.
  useEffect(() => {
    setPage(1);
  }, [search]);

  const handleCreate = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    await api.post('/clients', form);
    setForm(EMPTY_FORM);
    setShowForm(false);
    setSaving(false);
    load();
  };

  const handleDelete = async (c, ev) => {
    ev.stopPropagation();
    if (
      !confirm(
        `Supprimer le client "${c.name}" et tous ses sites/agents associés ? Cette action est irréversible.`
      )
    )
      return;
    await api.del('/clients/' + c.id);
    load();
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-5">
        <h1 className="text-[22px] font-bold text-fg flex-none">Clients</h1>
        <div className="flex-1" />
        {can('ManageClients') && (
          <button onClick={() => setShowForm(!showForm)} className={btnPrimary}>
            {showForm ? 'Annuler' : '+ Nouveau client'}
          </button>
        )}
      </div>

      {showForm && (
        <div className="bg-card border border-blue-500 rounded-2xl p-5 mb-4">
          <h3 className="text-sm font-semibold text-fg-2 mb-3.5">Nouveau client</h3>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label className={labelCls}>Nom *</label>
              <input
                className={inputCls}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Nom du client"
                onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
              />
            </div>
            <div>
              <label className={labelCls}>Email</label>
              <input
                className={inputCls}
                value={form.contactEmail}
                onChange={(e) => setForm({ ...form, contactEmail: e.target.value })}
                placeholder="contact@example.com"
              />
            </div>
            <div>
              <label className={labelCls}>Téléphone</label>
              <input
                className={inputCls}
                value={form.contactPhone}
                onChange={(e) => setForm({ ...form, contactPhone: e.target.value })}
                placeholder="+1 234 567 890"
              />
            </div>
            <div>
              <label className={labelCls}>Adresse</label>
              <input
                className={inputCls}
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                placeholder="123 rue Exemple"
              />
            </div>
          </div>
          {comit.enabled && (
            <div className="mb-3">
              <label className={labelCls}>Client ComIT lié</label>
              <ComitClientPicker
                value={form.comitClientId ? { id: form.comitClientId, name: form.comitClientName } : null}
                onChange={(next) =>
                  setForm({
                    ...form,
                    comitClientId: next ? next.id : 0,
                    comitClientName: next ? next.name : '',
                  })
                }
              />
            </div>
          )}
          <div className="mb-3.5">
            <label className={labelCls}>Notes</label>
            <textarea
              className={`${inputCls} min-h-[50px] resize-y`}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Notes internes..."
            />
          </div>
          <div className="flex gap-2.5">
            <button className={btnPrimary} onClick={handleCreate} disabled={saving || !form.name.trim()}>
              {saving ? 'Création...' : 'Créer le client'}
            </button>
            <button className={btnSecondary} onClick={() => setShowForm(false)}>
              Annuler
            </button>
          </div>
        </div>
      )}

      <div className="flex items-center gap-3 mb-4">
        <input
          className={inputCls}
          placeholder="Rechercher un client (nom, email)..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <span className="text-[11px] text-muted-2 whitespace-nowrap">
          {total.toLocaleString('fr-FR')} client{total !== 1 ? 's' : ''}
        </span>
      </div>

      {loading ? (
        <div className="text-muted-2">Chargement...</div>
      ) : clients.length === 0 ? (
        <div className="text-muted-2 text-[13px]">Aucun client trouvé</div>
      ) : (
        // Grille responsive : 1 colonne en étroit, 2 dès ~1024 px, 3 dès ~1536 px.
        // Sur un 16:9 courant (1920×1080) on obtient 3 colonnes, 2 sur un portable.
        <div className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-3">
          {clients.map((c) => (
            <div
              key={c.id}
              onClick={() => onSelectClient(c.id)}
              className="bg-card border border-rule rounded-2xl px-5 py-4 cursor-pointer transition-colors hover:border-blue-500 flex flex-col"
            >
              <div className="flex justify-between items-start gap-2 mb-2.5">
                <div className="min-w-0">
                  <div className="text-base font-bold text-fg truncate" title={c.name}>
                    {c.name}
                  </div>
                  {c.contactEmail && (
                    <div className="text-[11px] text-muted-2 mt-0.5 truncate" title={c.contactEmail}>
                      {c.contactEmail}
                    </div>
                  )}
                </div>
                <div className="flex gap-2 items-center flex-none">
                  {c.criticalAlerts > 0 && (
                    <span className="px-2.5 py-0.5 rounded-md text-[11px] font-bold bg-crit/15 text-crit whitespace-nowrap">
                      {c.criticalAlerts + ' critique' + (c.criticalAlerts > 1 ? 's' : '')}
                    </span>
                  )}
                  {can('ManageClients') && (
                    <button
                      onClick={(ev) => handleDelete(c, ev)}
                      title="Supprimer ce client"
                      className="bg-transparent border border-red-900 rounded-md px-2.5 py-1 text-crit text-[11px] cursor-pointer hover:bg-red-950/30 whitespace-nowrap"
                    >
                      Supprimer
                    </button>
                  )}
                </div>
              </div>
              {/* mt-auto : les métadonnées restent alignées en bas quand les cartes
                  d'une même rangée n'ont pas la même hauteur (email présent ou non). */}
              <div className="flex gap-x-5 gap-y-1 flex-wrap text-xs text-muted mt-auto">
                <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                  <Building2 size={13} strokeWidth={1.75} />
                  {c.sitesCount} site{c.sitesCount !== 1 ? 's' : ''}
                </span>
                <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                  <MonitorSmartphone size={13} strokeWidth={1.75} />
                  {c.onlineDevices}/{c.devicesCount} en ligne
                </span>
                {c.address && (
                  <span className="inline-flex items-center gap-1.5 min-w-0" title={c.address}>
                    <MapPin size={13} strokeWidth={1.75} className="flex-none" />
                    <span className="truncate">{c.address}</span>
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && totalPages > 1 && (
        <div className="flex justify-center items-center gap-1.5 mt-4">
          <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1} className={pageBtn(false, page === 1)}>
            ←
          </button>
          {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
            let p = i + 1;
            if (totalPages > 7) {
              if (page < 5) p = i + 1;
              else if (page > totalPages - 3) p = totalPages - 6 + i;
              else p = page - 3 + i;
            }
            return (
              <button key={p} onClick={() => setPage(p)} className={pageBtn(p === page, false)}>
                {p}
              </button>
            );
          })}
          <button
            onClick={() => setPage(Math.min(totalPages, page + 1))}
            disabled={page >= totalPages}
            className={pageBtn(false, page >= totalPages)}
          >
            →
          </button>
        </div>
      )}
    </div>
  );
}
